import React from 'react';
import { TextField, Grid, Divider } from "@mui/material";
import { Box } from "@mui/system";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import NCRFormHeading from '../NCRFormHeading';
import NCRFormLabel from '../NCRFormLabel';
import { DeekSeekIntegration } from '../DeepSeekIntegration';
import NCRAIIcon from '../NCRAIIcon';
import { validateAIRequiredFields } from '../../../Helpers/NCRHelpers';
import NCRMultilineTextfields from '../NCRMultilineTextfields';
import NCRTextField from '../NCRTextField';

export default function NonConformanceDescription({ content, onChange, aiResponses, setAiResponses }) {

  const theme = createTheme({
    components: {
      MuiSelect: {
        styleOverrides: {
          select: {
            fontSize: "14px",
            fontWeight: 400,
            color: "rgba(84, 84, 84, 1)",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            "&::placeholder": {
              fontSize: "14px",
              fontWeight: 400,
              color: "rgba(84, 84, 84, 1)",
              opacity: 1,
            },
          },
        },
      },
    },
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onChange({ [name]: value });
  };

  const handleAIClick = async (fieldName) => {
    const { isValid } = validateAIRequiredFields(fieldName, content);
    if (!isValid) return;

    let dotIndex = 0;
    const loadingDots = [" .", " ..", " ...", "$@!"];
    const interval = setInterval(() => {
      setAiResponses((prev) => ({
        ...prev,
        [fieldName]: loadingDots[dotIndex],
      }));
      dotIndex = (dotIndex + 1) % loadingDots.length;
    }, 500);
    try {
      const generatedResponse = await DeekSeekIntegration(fieldName, { [fieldName]: content.formData[fieldName] });
      clearInterval(interval);

      if (!generatedResponse || typeof generatedResponse !== "string") {
        setAiResponses((prev) => ({
          ...prev,
          [fieldName]: "",
        }));
        return;
      }

      let index = 0;
      setAiResponses((prev) => ({
        ...prev,
        [fieldName]: ".",
      }));

      const typingEffect = setInterval(() => {
        setAiResponses((prev) => ({
          ...prev,
          [fieldName]: generatedResponse.substring(0, index + 1),
        }));

        index++;
        if (index >= generatedResponse.length) {
          clearInterval(typingEffect);
        }
      }, 12);

    } catch (error) {
      clearInterval(interval);
      setAiResponses((prev) => ({
        ...prev,
        [fieldName]: "Error fetching AI response",
      }));
    }
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <Box>
          <Box sx={{ p: 3 }}>
            <Box>
              <NCRFormHeading tooltipData={
                [
                  { description: "Free text field to describe the nature of the non-conformance in detail." },
                ]
              }>
                Non conformance description
              </NCRFormHeading>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Grid container alignItems="center">
                      <Grid item xs={4}>
                        <NCRFormLabel isRequired={true} >What went wrong?</NCRFormLabel>
                      </Grid>
                      <Grid item xs={8}>
                        <NCRTextField
                          size="small"
                          sx={{ width: '100%' }}
                          multiline
                          name='what_went_wrong'
                          onChange={handleInputChange}
                          value={content.formData.what_went_wrong || ''}
                          disabled={content.inputDisable}
                          placeholder="Enter what went wrong?"
                          error={!!content?.errors?.what_went_wrong}
                          maxLength={250}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                {!content?.inputDisable && content?.label !== 'edit' &&
                  <Grid item xs={12} sm={5} >
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <NCRAIIcon label={"what_went_wrong"} aiResponse={aiResponses} handleAIClick={handleAIClick} />
                        <Box sx={{ width: '100%' }}>
                          <TextField
                            size="small"
                            className='ai-suggest-box'
                            sx={{
                              width: '100%',
                              caretColor: 'transparent',
                              backgroundColor: aiResponses?.["what_went_wrong"] ? 'rgba(235, 235, 235, 1)' : 'transparent',
                              "&:hover .MuiInputBase-input": {
                                cursor: aiResponses.what_went_wrong ? 'pointer' : 'default',
                                color: aiResponses.what_went_wrong ? 'rgba(42, 61, 189, 1) !Important' : '',
                                textDecoration: aiResponses.what_went_wrong ? 'underline' : 'none'
                              }
                            }}
                            multiline
                            placeholder={aiResponses.what_went_wrong === '$@!' ? '' : 'AI Suggestions will appear here'}
                            value={aiResponses.what_went_wrong === '$@!' ? '' : aiResponses.what_went_wrong}
                            onClick={() => {
                              if (aiResponses.what_went_wrong) {
                                onChange({ what_went_wrong: aiResponses.what_went_wrong });
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                }
              </Grid>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Grid container alignItems="center">
                      <Grid item xs={4}>
                        <NCRFormLabel isRequired={true} >How was the issue identified?</NCRFormLabel>
                      </Grid>
                      <Grid item xs={8}>
                        <NCRTextField
                          size="small"
                          sx={{ width: '100%' }}
                          multiline
                          name='how_was_issue_identified'
                          onChange={handleInputChange}
                          value={content.formData.how_was_issue_identified || ''}
                          disabled={content.inputDisable}
                          placeholder="Enter how was the issue identified?"
                          error={!!content?.errors?.how_was_issue_identified}
                          maxLength={250}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                {/* {!content?.inputDisable && content?.label !== 'edit' &&
                  <Grid item xs={12} sm={5} >
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <NCRAIIcon label={"how_was_issue_identified"} aiResponse={aiResponses} handleAIClick={handleAIClick} />
                        <Box sx={{ width: '100%' }}>
                          <TextField
                            size="small"
                            className='ai-suggest-box'
                            sx={{
                              width: '100%',
                              caretColor: 'transparent',
                              backgroundColor: aiResponses?.["how_was_issue_identified"] ? 'rgba(235, 235, 235, 1)' : 'transparent',
                              "&:hover .MuiInputBase-input": {
                                cursor: aiResponses.how_was_issue_identified ? 'pointer' : 'default',
                                color: aiResponses.how_was_issue_identified ? 'rgba(42, 61, 189, 1) !Important' : '',
                                textDecoration: aiResponses.how_was_issue_identified ? 'underline' : 'none'
                              }
                            }}
                            placeholder={aiResponses.how_was_issue_identified === '$@!' ? '' : 'AI Suggestions will appear here'}
                            value={aiResponses.how_was_issue_identified === '$@!' ? '' : aiResponses.how_was_issue_identified}
                            onClick={() => {
                              if (aiResponses.how_was_issue_identified) {
                                onChange({ how_was_issue_identified: aiResponses.how_was_issue_identified });
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                } */}
              </Grid>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Grid item xs={4}>
                <NCRFormLabel sx={{ mb: 1 }} >Relevant measurements, observations, or specifications.</NCRFormLabel>
              </Grid>
              <Grid item xs={8}>
                <NCRMultilineTextfields
                  name="relevant_measurements_observations_specification"
                  value={content.formData.relevant_measurements_observations_specification || ""}
                  onChange={handleInputChange}
                  disabled={content.inputDisable}
                  placeholder="Enter relevant measurements, observations, or specifications"
                />
              </Grid>
            </Box>
          </Box>
          <Divider sx={{ mt: 1.5, mb: 0.5 }} />
        </Box>
      </ThemeProvider >
    </>
  );
};

